import { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import tuvSrc from '@/assets/images/certificates/tuv.jpg';
import Button from '@/components/button';
import ButtonLink from '@/components/button-link';
import Logos from '@/components/logos';
import NumberedList from '@/components/numbered-list';
import SeePriceHead from '@/components/see-price-head';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import '@/modals/see-price/see-price-v1/see-price-v1.css';

export default function ModalSeePriceV1(props) {
  const { initialStep, onButtonClick, onMoreClick, onUpdateModifiers, modalHeight, scrollTop } = props;

  const [step, setStep] = useState(initialStep);
  const [seePriceHeight, setSeePriceHeight] = useState(0);
  const [explainFooterHeight, setExplainFooterHeight] = useState(0);
  const [isShadeVisible, setIsShadeVisible] = useState(true);

  const certificateLogos = useAppSelector(ConfigSlice.selectors.certificateLogos);

  const seePriceRef = useRef(null);
  const buttonRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      if (buttonRef.current && logoRef.current && seePriceRef.current) {
        const buttonHeight = buttonRef.current.getBoundingClientRect().height;
        const logoHeight = logoRef.current.getBoundingClientRect().height;
        const seePriceHeight = seePriceRef.current.getBoundingClientRect().height;
        setExplainFooterHeight(Math.round(buttonHeight + logoHeight));
        setSeePriceHeight(Math.round(seePriceHeight));
      }
    });
  }, []);

  useEffect(() => {
    if (scrollTop !== undefined && modalHeight !== undefined) {
      const disappearPoint = seePriceHeight - modalHeight - explainFooterHeight - 29;
      setIsShadeVisible(disappearPoint > scrollTop);
    }
  });

  useEffect(() => {
    if (step === 1) {
      onUpdateModifiers && onUpdateModifiers({ add: ['close-icon-white', 'see-price'] });
    }
    if (step === 2) {
      onUpdateModifiers && onUpdateModifiers({ add: ['see-price'], remove: ['close-icon-white'] });
    }
  }, [step]);

  const explainShadeBottom = (isShadeVisible) => {
    return clsx('see-price__explain-shade-bottom', {
      'see-price__explain-shade-bottom--visible': isShadeVisible,
    });
  };

  const explainShadeTop = (isShadeVisible) => {
    return clsx('see-price__explain-shade-top', {
      'see-price__explain-shade-top--visible': isShadeVisible,
    });
  };

  const onMore = () => {
    setStep(3);
    onMoreClick();
  };

  return (
    <div ref={seePriceRef} className='see-price'>
      {step === 1 && (
        <>
          <SeePriceHead version='default' />
          <div className='see-price__init'>
            <div className='see-price__init-title'>
              <span className='see-price__title'>So funktioniert’s</span>
            </div>
            <div className='see-price__init-list'>
              <NumberedList>
                <>
                  <b>Sie klicken auf den unteren Button,</b> um auf die Online Vollmacht zu gelangen.
                </>
                <>
                  Bei der Kostenübersicht <b>klicken Sie auf “Ich bin rechtschutzverischert”.</b>
                </>
                <>
                  <b>Sie erteilen uns die Vollmacht,</b> teilen uns Ihre Versicherungsdaten mit und wir stellen
                  automatisch die Deckungsanfrage bei Ihrer Versicherung.
                </>
              </NumberedList>
            </div>
            <div className='see-price__init-button'>
              <Button full color='primary' iconPosition='after' icon='arrow' onClick={onButtonClick}>
                Vollmacht ansehen
              </Button>
            </div>
            <div className='see-price__init-navigation'>
              <ButtonLink onClick={() => setStep(2)}>Doch nicht rechtsschutzversichert?</ButtonLink>
            </div>
            <div className='see-price__logos'>
              <Logos show={certificateLogos} tuvSrc={tuvSrc} />
            </div>
          </div>
        </>
      )}
      {step === 2 && (
        <div className='see-price__explain'>
          <div className={explainShadeTop(scrollTop !== undefined && scrollTop > 0)} />
          <div className='see-price__explain-block'>
            <div className='see-price__explain-title'>
              <h6 className='see-price__title'>Preis für Rechtsschutzversicherte</h6>
            </div>
            <p className='see-price__text'>
              Im besten Fall entstehen Ihnen dadurch gar keine zusätzlichen Kosten, denn{' '}
              <b>wir rechnen unser Honorar direkt mit Ihrer Rechtsschutzversicherung ab.</b>
              <br />
              <ButtonLink inherit onClick={() => setStep(1)}>
                Klicken Sie hier, wenn Sie bereits rechtsschutzverischert sind →
              </ButtonLink>
            </p>
          </div>
          <div className='see-price__explain-block'>
            <div className='see-price__explain-title'>
              <h6 className='see-price__title'>Preis für Nicht-Rechtsschutzversicherte</h6>
            </div>
            <div className='see-price__explain-paragraph'>
              <p className='see-price__text'>
                Sollten sie keine Rechtsschutzversicherung haben, gibt es <b>zwei Möglichkeiten:</b>
              </p>
            </div>
            <NumberedList>
              <>
                Sie schließen eine <b>rückwirkende Rechtsschutzversicherung</b> ab. Dies ist erst seit einigen Jahren
                möglich und bietet Ihnen den vollen Versicherungsschutz - auch für Fälle die bereits eingetreten sind.
                <br />
                <ButtonLink inherit onClick={onMore}>
                  Mehr erfahren →
                </ButtonLink>
              </>
              <>
                Wir vertreten Sie als Privatzahler für einen günstigen Pauschalpreis i.H.v. 500,- Euro inkl. MwSt. In
                dem Preis sind alle Kosten für das außergerichtliche Verfahren abgegolten. Sollte es zu einem
                Gerichtstermin kommen den wir für sie wahrnehmen sollen, fällt eine weitere Gebühr an. Hierüber
                informieren wir Sie rechtzeitig und helfen Ihnen bei der Entscheidung.
              </>
            </NumberedList>
          </div>
          <div ref={buttonRef} className='see-price__explain-button'>
            <Button color='primary' iconPosition='after' icon='arrow' onClick={onButtonClick}>
              Vollmacht ansehen
            </Button>
          </div>
          <div ref={logoRef}>
            <Logos show={certificateLogos} tuvSrc={tuvSrc} />
          </div>
          <div className={explainShadeBottom(isShadeVisible)} />
        </div>
      )}
      {step === 3 && (
        <div className='see-price__explain'>
          <div className='see-price__explain-title'>
            <h6 className='see-price__title'>Kostendeckung über eine rückwirkende Rechtsschutzversicherung</h6>
          </div>
          <div className='see-price__explain-block'>
            <p className='see-price__text'>
              Es ist seit einigen Jahren möglich, auch für Fälle die bereits eingetreten sind, eine rückwirkende
              Rechtsschutzversicherung abzuschließen, die sofort den vollen Schutz bietet.
            </p>
          </div>
          <div className='see-price__explain-block'>
            <p className='see-price__text'>
              Eine Möglichkeit in diese Richtung bietet die{' '}
              <strong>KS/AUXILIA mit dem Verkehrs-Rechtsschutz flex sofort</strong>.
            </p>
          </div>
          <div className='see-price__explain-block'>
            <p className='see-price__text'>
              Der <strong>KS/AUXILIA Verkehrs-Rechtsschutz flex sofort</strong> ist ab{' '}
              <strong>monatliche 32,50 €</strong> abschließbar und deckt alle Anwalts- und Gerichtskosten - auch wenn
              der Ärger schon da ist, ohne Wartezeit.
            </p>
          </div>
          <div className='see-price__explain-block'>
            <p className='see-price__text'>
              Klicken Sie unten wenn Sie mehr über diese Möglickkeit erfahren möchten bzw.{' '}
              <strong>einfach und schnell diese Versicherung gleich abschließen</strong> möchten.
            </p>
          </div>
          <div className='see-price__explain-block'>
            <p className='see-price__text'>
              <u>WICHTIG!</u> Die Vollmacht sollten Sie uns in diesem Fall über den Link den wir soeben an Ihre
              E-Mailadresse gesendet haben,{' '}
              <strong>erst nachdem Sie den rückwirkenden Rechtsschutz abgeschlossen haben</strong>, erteilen. Sonst
              könnte die Versicherung die Kostenübernahme verweigern.
            </p>
          </div>
          <div className='see-price__explain-button'>
            <Button
              href='https://www.ks-auxilia.de/beitrags-rechner-rightmart'
              color='primary'
              iconPosition='after'
              icon='arrow'
              full
            >
              Zur KS/AUXILIA Versicherung
            </Button>
          </div>
          <div ref={buttonRef} className='see-price__explain-button'>
            <button className='see-price__link' onClick={() => setStep(2)}>
              Zurück
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

ModalSeePriceV1.displayName = 'ModalSeePriceV1';
